import { Language } from '../../dto/language';
import { type WordToRepeatDto, type QuizStep, type GetQuizOptionsRequest } from '../../dto/user-words';
import { IEnv } from '../../infrastructure/i-env';
import { ICurrentUser } from '../../infrastructure/i-current-user';
import { IQuizzService } from '../../services/i-quizz-service';
import type { QuizWord } from './use-quizz';

function fallbackQuizWords(wordsToQuiz: WordToRepeatDto[], maxWords: number): QuizWord[] {
	return wordsToQuiz.slice(0, maxWords).map(word => ({
		validVariant: {
			id: word.id,
			original: word.word,
			translation: word.translation,
			transliteration: word.transliteration ?? undefined
		},
		invalidVariants: []
	}));
}

/**
 * Convert backend quiz steps into QuizWord format.
 */
export function convertQuizStepsToQuizWords(steps: QuizStep[]): QuizWord[] {
	return steps.map(step => ({
		validVariant: {
			id: step.correctWord.id,
			original: step.correctWord.sourceWord,
			translation: step.correctWord.translation,
			transliteration: step.correctWord.transliteration
		},
		invalidVariants: step.invalidOptions.map(opt => ({
			original: step.correctWord.sourceWord,
			translation: opt.translation,
			transliteration: opt.transliteration
		}))
	}));
}

/**
 * Generate quiz words using backend API for words to repeat.
 * All framework dependencies are provided via IEnv/ICurrentUser.
 */
export async function generateQuizWordsFromUserCollection(
	env: IEnv,
	currentUser: ICurrentUser,
	wordsToQuiz: WordToRepeatDto[],
	nativeLanguage: Language,
	learnedLanguage: Language,
	maxWords: number = 10
): Promise<QuizWord[]> {
	try {
		await currentUser.initialize();
		const userId = currentUser.getUserId();
		if (!userId) {
			return fallbackQuizWords(wordsToQuiz, maxWords);
		}

		const quizzService = env.get(IQuizzService);
		const request: GetQuizOptionsRequest = {
			userId,
			nativeLanguage,
			learnedLanguage,
			mode: 'to-repeat',
			maxSteps: maxWords
		};

		const response = await quizzService.getQuizOptions(request);
		return convertQuizStepsToQuizWords(response.steps);
	} catch (error) {
		env.log('Error generating quiz words', error);
		return fallbackQuizWords(wordsToQuiz, maxWords);
	}
}

/**
 * Generate quiz words for an article using backend API.
 * Returns [] for unauthenticated users (same behavior as the previous helper).
 */
export async function generateQuizWordsForArticle(
	env: IEnv,
	currentUser: ICurrentUser,
	articleTranslationId: string,
	nativeLanguage: Language,
	learnedLanguage: Language,
	maxWords: number = 7
): Promise<QuizWord[]> {
	try {
		await currentUser.initialize();
		const userId = currentUser.getUserId();
		if (!userId) {
			return [];
		}

		const quizzService = env.get(IQuizzService);
		const request: GetQuizOptionsRequest = {
			userId,
			nativeLanguage,
			learnedLanguage,
			mode: 'article',
			articleTranslationId,
			maxSteps: maxWords
		};

		const response = await quizzService.getQuizOptions(request);
		return convertQuizStepsToQuizWords(response.steps);
	} catch (error) {
		env.log('Error generating quiz words for article', error);
		return [];
	}
}


